import React from 'react';
import {
  ClipboardList, Loader2, CheckCircle, AlertCircle, Clock,
  Cloud, CloudOff, FileEdit, RefreshCw, X,
} from 'lucide-react';

const AUTO_SAVE_LABELS = {
  saving: { Icon: Loader2, color: 'text-blue-400', text: 'Saving...', spin: true },
  saved: { Icon: CheckCircle, color: 'text-green-400', text: 'Auto-saved' },
  error: { Icon: AlertCircle, color: 'text-red-400', text: 'Save failed' },
};

const AutoSaveIndicator = ({ status, hasUnsavedChanges }) => {
  if (status === 'idle') {
    if (!hasUnsavedChanges) return null;
    return (
      <>
        <Clock className="w-3 h-3 text-yellow-400" />
        <span className="text-yellow-400">Unsaved</span>
      </>
    );
  }
  const cfg = AUTO_SAVE_LABELS[status];
  if (!cfg) return null;
  const { Icon, color, text, spin } = cfg;
  return (
    <>
      <Icon className={`w-3 h-3 ${color} ${spin ? 'animate-spin' : ''}`} />
      <span className={color}>{text}</span>
    </>
  );
};

export const CallDetailsHeader = ({
  currentDraftId,
  autoSaveEnabled,
  autoSaveStatus,
  hasUnsavedChanges,
  showDrafts,
  draftsCount,
  onToggleAutoSave,
  onToggleDrafts,
  onClearForm,
  onClose,
}) => (
  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-b border-gray-800 px-6 py-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-500/20 rounded-lg">
          <ClipboardList className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h2 className="text-white font-semibold text-lg">
            Call Details Form
            {currentDraftId && <span className="ml-2 text-xs text-yellow-400">(Editing Draft)</span>}
          </h2>
          <div className="flex items-center gap-2">
            <p className="text-gray-400 text-sm">Capture customer information from the call</p>
            {autoSaveEnabled && (
              <div className="flex items-center gap-1 text-xs">
                <AutoSaveIndicator status={autoSaveStatus} hasUnsavedChanges={hasUnsavedChanges} />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onToggleAutoSave}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            autoSaveEnabled
              ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
              : 'bg-gray-800 text-gray-500 hover:bg-gray-700'
          }`}
          title={autoSaveEnabled ? 'Auto-save enabled (every 30s)' : 'Auto-save disabled'}
          data-testid="auto-save-toggle-btn"
        >
          {autoSaveEnabled ? <Cloud className="w-3.5 h-3.5" /> : <CloudOff className="w-3.5 h-3.5" />}
          {autoSaveEnabled ? 'Auto' : 'Manual'}
        </button>
        <button
          onClick={onToggleDrafts}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            showDrafts
              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          data-testid="drafts-toggle-btn"
        >
          <FileEdit className="w-4 h-4" />
          Drafts
          {draftsCount > 0 && (
            <span className="px-1.5 py-0.5 bg-yellow-500/30 rounded-full text-xs">{draftsCount}</span>
          )}
        </button>
        {currentDraftId && (
          <button
            onClick={onClearForm}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-gray-300 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
            data-testid="new-form-btn"
          >
            <RefreshCw className="w-4 h-4" />
            New
          </button>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="call-details-close-btn"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        )}
      </div>
    </div>
  </div>
);
